import { useNavigate } from 'react-router-dom';
import { FormikHelpers, useFormik } from 'formik';
import { FaPaperPlane } from 'react-icons/fa6';
import { useIntl } from 'react-intl';
import { AxiosError } from 'axios';
import { ReactNode, useState } from 'react';

import axios from '@axios';
import type { FormLogin, ResponseLogin } from '@/pages/Auth/AuthTypes';
import InitialState from '@pages/Auth/AuthInitial.state';
import { useAuthenticate } from '@/context/AuthenticateContext';
import useDocumentTitle from '@/@core/hooks/useDocumentTitle';
import { Button, ErrorMessage, Input } from '@/@core';
import { InputPassword } from '@/@core/SuperInputPassword';
import { LoginValidationSchema } from '../AuthValidation.schema';
import { INITIAL_PATH } from '@/constants';

export const LoginPage = (): ReactNode => {
  const [error, setError] = useState<string>('');
  const { formatMessage: $t } = useIntl();
  const { login } = useAuthenticate();
  const navigate = useNavigate();

  useDocumentTitle($t({ id: 'Sign In' }));

  const onSubmit = async (formData: FormLogin, { setSubmitting }: FormikHelpers<FormLogin>) => {
    try {
      setSubmitting(true);
      const response = await axios.post<ResponseLogin>(`/login`, formData);
      const { token, data } = response.data;
      login({ token, user: data });
      navigate(`${INITIAL_PATH}/api-clients`);
    } catch (error) {
      if (error instanceof AxiosError) {
        setError(error.response?.data?.error || 'An error has occurred');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const { handleSubmit, handleChange, values, errors, isSubmitting } = useFormik<FormLogin>({
    validationSchema: LoginValidationSchema,
    initialValues: InitialState.FormLogin,
    validateOnChange: false,
    onSubmit,
  });

  return (
    <>
      <form onSubmit={handleSubmit} className="w-full max-w-sm rounded-lg bg-[#0E1120] p-10">
        <div className="mb-8">
          <img
            src={`${INITIAL_PATH}/images/gtl-logo-larger.png`}
            className="mx-auto h-[60px] w-auto"
            alt=""
          />
        </div>
        <div>
          <ErrorMessage error={error} />
          <div className="mb-3">
            <Input
              label="Email"
              name="email"
              onChange={handleChange}
              error={errors.email}
              value={values.email}
            />
          </div>
          <div className="mb-1">
            <InputPassword
              label="Password"
              name="password"
              onChange={handleChange}
              error={errors.password}
              value={values.password}
            />
          </div>
          <div className="space-x-1">
            <input
              type="checkbox"
              name="rememberMe"
              id="remember"
              className="m-0 cursor-pointer align-middle accent-current"
              onChange={handleChange}
              checked={values.rememberMe}
            />
            <label htmlFor="remember" className="cursor-pointer select-none text-xs font-bold">
              Remember me
            </label>
          </div>
        </div>
        <div className="mt-6">
          <Button
            type="submit"
            color="danger"
            className="w-full justify-center gap-2"
            disabled={isSubmitting}
          >
            <span>{$t({ id: !isSubmitting ? 'Sign In' : 'validating' })}</span> <FaPaperPlane />
          </Button>
        </div>
      </form>
    </>
  );
};
